import React from 'react';
import { FolderNode, FolderType } from '@teetsh/app/src/types/sequences';
import { ITreeNode } from '@teetsh/app/src/components/Tree2/types';
import { TreeIcons } from '@teetsh/app/src/components/Tree2/NodeIcon';
import { SecondaryLabels } from '@teetsh/app/src/containers/Sequences/List/Sidebar/SecondaryLabels';
import _last from 'lodash-es/last';
import { isDefined } from 'types/utils';
import { TFunction } from 'i18next';

export interface NodeData {
  parents: string[];
  type: FolderType;
}

interface ParentType {
  id: string;
  type: FolderType;
}

export function buildConfig(
  t: TFunction<'sequence'>,
): Record<
  FolderType,
  { defaultTitle: string; icon: TreeIcons; allowedTargetTypes: FolderType[] }
> {
  return {
    fdp: {
      defaultTitle: t('fileTree.defaultLabel.fdp'),
      icon: 'document',
      allowedTargetTypes: ['sequence'], // TODO later : ability to move it out of a sequence (=detach)
    },
    folder: {
      defaultTitle: t('fileTree.defaultLabel.folder'),
      icon: 'folder-close',
      allowedTargetTypes: ['folder'],
    },
    sequence: {
      defaultTitle: t('fileTree.defaultLabel.sequence'),
      icon: 'collection',
      allowedTargetTypes: ['folder'],
    },
    fdp_standalone: {
      defaultTitle: t('fileTree.defaultLabel.fdp_standalone'),
      icon: 'document',
      allowedTargetTypes: ['folder', 'sequence'],
    },
  };
}

export function computeNodes({
  selectedItemId,
  changeOrderFdp,
  onAddFdp,
  visibleTypes,
  hideActions,
  folders,
  parents = [],
  previewFdp,
  showAddFdpButton,
  hiddenActions,
  currentPath = '',
  config,
  rootOptionLabel,
}: {
  selectedItemId?: string;
  changeOrderFdp?: (
    index: number,
    direction: -1 | 1,
    items: string[],
    sequenceId: string,
  ) => void;
  onAddFdp?: (ficheDePrepId: string, sequenceId?: string) => void;
  onCreateFolder?: (path: string) => void;
  visibleTypes: FolderType[];
  hideActions?: boolean;
  folders: FolderNode[];
  parents?: ParentType[];
  previewFdp?: boolean;
  showAddFdpButton?: boolean;
  hiddenActions?: 'all';
  currentPath?: string;
  config: Record<
    FolderType,
    { defaultTitle: string; icon: TreeIcons; allowedTargetTypes: FolderType[] }
  >;
  rootOptionLabel?: string;
}): Array<ITreeNode<NodeData>> {
  const result: (ITreeNode<NodeData> | undefined)[] = folders.map(
    (folder, index) => {
      if (!visibleTypes.includes(folder.type)) {
        return undefined;
      }
      const isExpanded = folder.id === selectedItemId;
      const placeholderItemInFolder: ITreeNode<NodeData>[] =
        folder.type === 'folder'
          ? [
              {
                id: currentPath,
                isPlaceholder: true,
                label: '',
                placeholderId: folder.id,
                isExpanded: false,
                childNodes: [],
              },
            ]
          : [];

      const node: ITreeNode<NodeData> = {
        id: folder.id,
        label: folder.title || config[folder.type].defaultTitle,
        isSelected: folder.id === selectedItemId,
        isExpanded,
        isDisabledOver: activeItem =>
          !activeItem?.type ||
          !config[activeItem.type]?.allowedTargetTypes.includes(folder.type),
        secondaryLabel: hideActions ? null : (
          <SecondaryLabels
            folderId={folder.id}
            type={folder.type}
            changeOrderFdp={changeOrderFdp}
            onAddFdp={onAddFdp}
            index={index}
            folders={folders}
            parents={parents.map(p => p.id)}
            previewFdp={previewFdp}
            hiddenActions={hiddenActions}
            showAddFdpButton={showAddFdpButton}
          />
        ),
        icon: config[folder.type].icon,
        hasCaret:
          (folder.children || []).filter(c => visibleTypes.includes(c.type))
            .length > 0,
        nodeData: {
          type: folder.type,
          parents: parents.map(p => p.id),
        },
        childNodes: folder.children
          ? computeNodes({
              selectedItemId: selectedItemId,
              changeOrderFdp,
              onAddFdp,
              visibleTypes,
              hideActions,
              folders: folder.children,
              parents: [...parents, { id: folder.id, type: folder.type }],
              previewFdp,
              showAddFdpButton,
              hiddenActions,
              currentPath: `${currentPath}.${index}`,
              config,
            })
          : placeholderItemInFolder,
      };

      return node;
    },
  );

  const parent = _last(parents);
  // Add an empty folder at the start and end of the result as drag and drop placeholders
  result.unshift({
    id: `${currentPath}.${0}`,
    label: '',
    placeholderId: parent?.id,
    isDisabledOver: activeItem =>
      !activeItem?.type ||
      !config[activeItem.type]?.allowedTargetTypes.includes('folder'),
    isExpanded: false,
    isPlaceholder: true,
    nodeData: {
      type: 'folder',
      parents: parents.map(p => p.id),
    },
    childNodes: [],
  });

  result.push({
    id: `${currentPath}.${folders.length + 1}`,
    label: parent?.id!,
    placeholderId: parent?.id,
    isDisabledOver: activeItem =>
      !activeItem?.type ||
      !config[activeItem.type]?.allowedTargetTypes.includes('folder'),
    isExpanded: false,
    isPlaceholder: true,
    nodeData: {
      type: 'folder',
      parents: parents.map(p => p.id),
    },
    childNodes: [],
  });

  if (rootOptionLabel) {
    result.unshift({
      id: 'root',
      label: rootOptionLabel,
      icon: 'root',
      placeholderId: parent?.id,
      isDisabledOver: activeItem =>
        !activeItem?.type ||
        !config[activeItem.type]?.allowedTargetTypes.includes('folder'),
      isExpanded: false,
      isPlaceholder: false,
      isSelected: selectedItemId === 'root',
      nodeData: {
        type: 'folder',
        parents: parents.map(p => p.id),
      },
      childNodes: [],
    });
  }

  return result.filter(isDefined);
}
